import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDownIcon, SparklesIcon, CornerDownRightIcon, XIcon } from 'lucide-react';
import { RoomDetail, Room } from './RoomDetail';
import { RoomScene, SpaRoom } from './RoomScene';
import { HallwayScene, HallwayRoomId } from './HallwayScene';
import { Logo } from './Logo';
const HALLWAY_IMG = 'https://cdn.magicpatterns.com/uploads/ecbShRgCAdbT1ndjDFTduZ/ChatGPT_Image_Jun_1,_2026,_04_49_33_PM.png';
const FLYER_IMG = 'https://cdn.magicpatterns.com/uploads/6mchqsAJGwZZekvTUeZJPq/IMG-20260601-WA0007_310868963557398[1].jpg';
// Laser Hair Removal room (green carpet, DioLux device) — white doors + gold handle
const laserRoom: SpaRoom = {
  id: 'laser',
  name: 'Laser Hair Removal',
  tagline: 'Smooth & Effortless',
  blurb: 'Smooth skin. No razor. No stress. Just confidence.',
  description: 'A private, comfortable room with our advanced DioLux laser. Say goodbye to razors and waxing — walk out with skin that feels smooth, soft, and effortlessly confident.',
  features: ['Long-lasting smooth skin', 'No more razors or waxing', 'Quick, comfortable sessions', 'Confidence you can feel'],
  photos: ['https://cdn.magicpatterns.com/uploads/hce2ssbWRLPGbbR7ND3MpN/20260525_142505.jpg', 'https://cdn.magicpatterns.com/uploads/fjG7gzpE29qqnubpMy4B6g/20260525_142339[1].jpg'],
  video: 'https://cdn.magicpatterns.com/uploads/cWk4fNx4yhJDHxd6bVjjSm/kling_20260602_VIDEO__Image1_I__1398_0.mp4'
};
// Biocharging room — 3rd door on the left
const biochargingRoom: SpaRoom = {
  id: 'biocharging',
  name: 'Biocharging',
  tagline: 'Energy & Renewal',
  blurb: 'Recharge your cells. Boost your energy. Feel alive.',
  description: 'A calm, restorative room where you recharge at the cellular level — step out feeling lighter, brighter, and fully alive.',
  features: ['Cellular energy recharge', 'Supports natural vitality', 'Calm, restorative session', 'Feel renewed from the inside out'],
  photos: ['https://cdn.magicpatterns.com/uploads/vpa8ss6yRZ2AC99meqyi2a/20260525_142510[1].jpg'],
  video: 'https://cdn.magicpatterns.com/uploads/8fWBmWYM4aByGNtefMiLTb/kling_20260602_VIDEO__Image1_I__1332_0.mp4'
};
// Body Sculpting room — 2nd door on the left
const sculptingRoom: SpaRoom = {
  id: 'sculpting',
  name: 'Body Sculpting',
  tagline: 'Tone & Define',
  blurb: 'Tone, tighten and sculpt your best you.',
  description: 'A private treatment room where advanced sculpting helps tone, tighten, and define. This room also houses our detox foot soaking supplies, conveniently located under the bed for a complete wellness experience.',
  features: ['Tones and tightens', 'Sculpts and defines', 'Detox foot soaking available', 'Comfortable, relaxing experience'],
  photos: [],
  video: 'https://cdn.magicpatterns.com/uploads/25ngsk8skx2zwJ5tG2BGZ9/kling_20260602_VIDEO__Image1_I__1393_0.mp4'
};
// Real services from the flyer — easy to extend/reorder as hallway rooms are added
const services: Room[] = [{
  id: 'biocharging',
  name: 'Biocharging',
  tagline: 'Energy & Renewal',
  blurb: 'Recharge your cells. Boost your energy. Feel alive.',
  description: 'A gentle cellular recharge that helps restore your natural energy and vitality. Step out feeling lighter, brighter, and fully alive.',
  features: ['Cellular energy recharge', 'Supports natural vitality', 'Calm, restorative session', 'Feel renewed from the inside out'],
  image: FLYER_IMG
}, {
  id: 'sculpting',
  name: 'Sculpting',
  tagline: 'Tone & Define',
  blurb: 'Tone, tighten and sculpt your best you.',
  description: 'A contouring treatment designed to tone, tighten, and sculpt — helping you feel confident and beautiful in your own skin.',
  features: ['Tones and tightens', 'Sculpts and defines', 'Comfortable, relaxing experience', 'Confidence-boosting results'],
  image: FLYER_IMG
}, {
  id: 'laser',
  name: 'Laser Hair Removal',
  tagline: 'Smooth & Effortless',
  blurb: 'Smooth skin. No razor. No stress. Just confidence.',
  description: 'Say goodbye to razors and stress. Our laser hair removal leaves your skin smooth, soft, and effortlessly confident.',
  features: ['Long-lasting smooth skin', 'No more razors or waxing', 'Quick, comfortable sessions', 'Confidence you can feel'],
  image: FLYER_IMG
}, {
  id: 'detox',
  name: 'Full Body Detox',
  tagline: 'Reset & Refresh',
  blurb: 'Flush toxins. Reset your body. Feel lighter & brighter.',
  description: 'A full-body detox to flush toxins and reset your system, leaving you feeling lighter, brighter, and beautifully refreshed.',
  features: ['Flushes toxins', 'Resets and refreshes the body', 'Feel lighter and brighter', 'A true reset for body and mind'],
  image: FLYER_IMG
}];
export function SpaLobby() {
  const [selected, setSelected] = useState<Room | null>(null);
  const [roomScene, setRoomScene] = useState<SpaRoom | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hallwayOpen, setHallwayOpen] = useState(false);
  const handlePickRoom = (id: HallwayRoomId) => {
    setHallwayOpen(false);
    if (id === 'laser') setRoomScene(laserRoom);else if (id === 'sculpting') setRoomScene(sculptingRoom);else if (id === 'biocharging') setRoomScene(biochargingRoom);
  };
  // Candle / item glows over the wood demilune table
  const candles = useMemo(() => [{
    x: 29,
    y: 80,
    size: 12
  }, {
    x: 50,
    y: 82,
    size: 10
  }, {
    x: 60,
    y: 80,
    size: 12
  } // singing bowl glow
  ], []);
  const sparkles = useMemo(() => Array.from({
    length: 14
  }).map((_, i) => ({
    id: i,
    left: 10 + Math.random() * 80,
    top: 30 + Math.random() * 55,
    size: 6 + Math.random() * 10,
    duration: 2.5 + Math.random() * 3,
    delay: Math.random() * 4
  })), []);
  return <section id="lobby" className="relative w-full overflow-hidden bg-aubergine-900" style={{
    minHeight: '100vh'
  }} data-id="element-227">
      {/* Real hallway-table photo — full-bleed immersive look */}
      <div className="absolute inset-0" data-id="element-228">
        <img src={HALLWAY_IMG} alt="Wonder of Life Wellness Spa — the wood console table with copper wave art, the Wonder of Life logo decal, and the hallway opening to the treatment rooms" className="w-full h-full object-cover" data-id="element-229" />
        <div className="absolute inset-0 bg-gradient-to-b from-aubergine-900/15 via-transparent to-aubergine-900/45" data-id="element-230" />
      </div>

      {/* Right-side hallway opening hotspot — leads down to the rooms */}
      <button onClick={() => setHallwayOpen(true)} className="absolute group focus:outline-none cursor-pointer z-20" style={{
      right: '6%',
      top: '50%',
      width: '14%',
      height: '60%',
      transform: 'translateY(-50%)'
    }} aria-label="Walk down the hall to the treatment rooms" data-id="element-231">
        <motion.div animate={{
        opacity: [0.3, 0.6, 0.3]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute inset-0 pointer-events-none rounded-lg" style={{
        background: 'radial-gradient(ellipse at center, rgba(224,196,126,0.45) 0%, transparent 70%)',
        filter: 'blur(14px)'
      }} data-id="element-232" />
        <div className="absolute inset-0 rounded-lg border border-gold-300/40 group-hover:border-gold-300 transition-colors" style={{
        boxShadow: '0 0 20px rgba(224,196,126,0.3)'
      }} data-id="element-233" />
      </button>

      {/* Gold sparkle glam over the scene */}
      {sparkles.map(s => <motion.div key={s.id} animate={{
      opacity: [0, 0.9, 0],
      scale: [0.4, 1, 0.4]
    }} transition={{
      duration: s.duration,
      repeat: Infinity,
      delay: s.delay,
      ease: 'easeInOut'
    }} className="absolute pointer-events-none z-10" style={{
      left: `${s.left}%`,
      top: `${s.top}%`
    }} data-id="element-234">
          <SparklesIcon size={s.size} className="text-gold-300" style={{
        filter: 'drop-shadow(0 0 5px rgba(224,196,126,0.8))'
      }} data-id="element-235" />
        </motion.div>)}

      {/* Primary CTA into the hallway */}
      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6,
      duration: 1
    }} className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-3" data-id="element-236">
        <button onClick={() => setHallwayOpen(true)} className="group inline-flex items-center gap-3 px-9 py-4 rounded-full bg-aubergine-800/90 backdrop-blur border border-gold-400/40 text-cream-50 text-[11px] uppercase tracking-luxe hover:bg-aubergine-700 transition-all duration-500 shadow-[0_0_40px_rgba(58,31,77,0.6)]" data-id="element-237">
          <CornerDownRightIcon size={16} className="text-gold-300" data-id="element-238" />
          Walk Down the Hall
        </button>
      </motion.div>

      {/* Scroll cue */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-1 pointer-events-none" data-id="element-239">
        <span className="text-[8px] uppercase tracking-[0.4em] text-cream-50/60" data-id="element-240">
          More below
        </span>
        <motion.div animate={{
        y: [0, 5, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} className="text-gold-300/80" data-id="element-241">
          <ArrowDownIcon size={12} strokeWidth={1.4} data-id="element-242" />
        </motion.div>
      </div>

      {/* Services menu overlay (down the hall) */}
      <AnimatePresence data-id="element-243">
        {menuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} className="fixed inset-0 z-[150] flex items-center justify-center p-4 md:p-8" data-id="element-244">
            <div className="absolute inset-0 bg-aubergine-900/90 backdrop-blur-md" onClick={() => setMenuOpen(false)} data-id="element-245" />
            <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.5
        }} className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-id="element-246">
              <button onClick={() => setMenuOpen(false)} aria-label="Close menu" className="absolute top-0 right-0 w-10 h-10 rounded-full bg-cream-50/90 flex items-center justify-center hover:bg-lilac-100 transition-colors z-20" data-id="element-247">
                <XIcon size={18} className="text-ink" data-id="element-248" />
              </button>

              <div className="text-center mb-8" data-id="element-249">
                <div className="flex items-center justify-center gap-3 mb-4" data-id="element-250">
                  <SparklesIcon size={14} className="text-gold-300" data-id="element-251" />
                  <span className="text-[10px] uppercase tracking-luxe text-gold-300" data-id="element-252">
                    Down the Hall
                  </span>
                  <SparklesIcon size={14} className="text-gold-300" data-id="element-253" />
                </div>
                <h2 className="font-serif text-4xl md:text-5xl text-cream-50" data-id="element-254">
                  Our Services
                </h2>
                <p className="text-cream-50/60 text-sm font-light mt-3" data-id="element-255">
                  Choose a treatment to learn more.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4" data-id="element-256">
                {services.map((s, i) => <motion.button key={s.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: i * 0.08
            }} onClick={() => {
              setMenuOpen(false);
              if (s.id === 'laser') {
                setRoomScene(laserRoom);
              } else {
                setSelected(s);
              }
            }} className="group text-left p-7 rounded-sm bg-cream-50 border border-gold-400/20 hover:border-gold-400/60 transition-all duration-400" data-id="element-257">
                    <h3 className="font-serif text-2xl text-ink mb-2 group-hover:text-aubergine-700 transition-colors" data-id="element-258">
                      {s.name}
                    </h3>
                    <p className="font-serif italic text-aubergine-700/80 text-base mb-3" data-id="element-259">
                      {s.blurb}
                    </p>
                    <span className="text-[10px] uppercase tracking-luxe text-gold-500" data-id="element-260">
                      {s.id === 'laser' ? 'Walk in →' : 'Learn more →'}
                    </span>
                  </motion.button>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <HallwayScene open={hallwayOpen} onClose={() => setHallwayOpen(false)} onPickRoom={handlePickRoom} data-id="element-261" />
      <RoomDetail room={selected} onClose={() => setSelected(null)} data-id="element-262" />
      <RoomScene room={roomScene} onClose={() => setRoomScene(null)} data-id="element-263" />
    </section>;
}